// Testimonials (sections/af-testimonials.liquid): an endless row of cards that drifts slowly to the left. The set of
// cards is copied enough times to cover the screen twice, and the row's position wraps by one set's width, so the
// loop has no seam. It eases to a stop while hovered, holds while touched, can be dragged or swiped (a flick carries
// on and settles back into the drift) and follows sideways trackpad scrolling. With Reduce Motion it doesn't drift
// but can still be dragged. It only runs while on screen. On every screen: brought back on 31 Oct 2026 (Sufyan) after a
// day as a still swipe row on phones; the cards are flat now (their glass no longer tilts in 3D towards the pointer,
// which made each card its own layer for the GPU and helped crash iPhone Safari when zoomed), so the moving row is
// the only layer.

const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

class AfTestimonials extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-af-testi-viewport]');
    this.track = this.querySelector('[data-af-testi-track]');
    this.set = this.querySelector('[data-af-testi-set]');
    if (!this.viewport || !this.track || !this.set || !this.set.children.length) return;
    this.speed = Number(this.dataset.speed) || 30;
    this.x = 0;
    this.velocity = 0;
    this.hovered = false;
    this.dragging = false;
    this.visible = false;
    this.resumeAt = 0;
    this.tick = this.tick.bind(this);

    this.fill();
    this.sizes = new ResizeObserver(() => this.fill());
    this.sizes.observe(this.viewport);
    this.sizes.observe(this.set);
    this.seen = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) this.start();
    });
    this.seen.observe(this);
    this.setUpPointer();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.sizes?.disconnect();
    this.seen?.disconnect();
  }

  // Copies of the set (hidden from screen readers and the keyboard) until the row covers the screen twice
  fill() {
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.period = this.set.getBoundingClientRect().width + gap;
    if (!this.period) return;
    const needed = Math.max(1, Math.ceil((this.viewport.clientWidth * 2) / this.period));
    const copies = [...this.track.querySelectorAll('[data-af-testi-copy]')];
    copies.slice(needed).forEach((copy) => copy.remove());
    for (let i = copies.length; i < needed; i += 1) {
      const copy = this.set.cloneNode(true);
      copy.removeAttribute('data-af-testi-set');
      copy.dataset.afTestiCopy = '';
      copy.setAttribute('aria-hidden', 'true');
      copy.inert = true;
      copy.querySelectorAll('[data-shopify-editor-block]').forEach((card) => card.removeAttribute('data-shopify-editor-block'));
      this.track.append(copy);
    }
    this.wrap();
    this.paint();
  }

  start() {
    if (this.frame) return;
    this.last = performance.now();
    this.frame = requestAnimationFrame(this.tick);
  }

  tick(now) {
    this.frame = 0;
    const seconds = Math.min(64, now - this.last) / 1000;
    this.last = now;
    if (!this.dragging) {
      const drift = reducedMotion() || this.hovered || now < this.resumeAt ? 0 : -this.speed;
      // The speed eases towards the drift (or a stop), so a flick slows down smoothly and a hover pauses gently
      this.velocity += (drift - this.velocity) * Math.min(1, seconds * 2.5);
      if (Math.abs(this.velocity) < 0.05 && drift === 0) this.velocity = 0;
      this.x += this.velocity * seconds;
      this.wrap();
      this.paint();
    }
    if (this.visible) this.frame = requestAnimationFrame(this.tick);
  }

  // Keeps the position within one set's width (0 to minus the width): the copies make every point look the same
  wrap() {
    if (!this.period) return;
    this.x %= this.period;
    if (this.x > 0) this.x -= this.period;
  }

  paint() {
    const x = this.x.toFixed(2);
    if (x === this.painted) return;
    this.painted = x;
    this.track.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  setUpPointer() {
    const viewport = this.viewport;
    viewport.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.hovered = true;
    });
    viewport.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.hovered = false;
    });
    viewport.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, from: this.x, lastX: event.clientX, lastTime: event.timeStamp, flick: 0 };
      if (event.pointerType !== 'mouse') this.resumeAt = Infinity;
    });
    viewport.addEventListener('pointermove', (event) => {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!this.dragging) {
        // Mostly sideways and past a few pixels: a drag. Otherwise it's a tap or the page scrolling.
        if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(event.clientY - drag.y)) return;
        this.dragging = true;
        this.velocity = 0;
        try {
          viewport.setPointerCapture(event.pointerId);
        } catch {
          // The pointer is already gone (or synthetic); the drag still follows the moves
        }
        viewport.classList.add('is-dragging');
      }
      const elapsed = Math.max(1, event.timeStamp - drag.lastTime);
      drag.flick = ((event.clientX - drag.lastX) / elapsed) * 1000;
      drag.lastX = event.clientX;
      drag.lastTime = event.timeStamp;
      this.x = drag.from + dx;
      this.wrap();
      drag.from = this.x - dx;
      this.paint();
    });
    const end = (event) => {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      this.drag = null;
      if (this.dragging) {
        this.dragging = false;
        viewport.classList.remove('is-dragging');
        viewport.classList.add('was-dragged');
        setTimeout(() => viewport.classList.remove('was-dragged'));
        this.velocity = Math.max(-2400, Math.min(2400, drag.flick));
        this.last = performance.now();
      }
      // After a touch the row waits a moment before drifting again
      this.resumeAt = event.pointerType === 'mouse' ? 0 : performance.now() + 1600;
      this.start();
    };
    viewport.addEventListener('pointerup', end);
    viewport.addEventListener('pointercancel', end);
    // A drag isn't a click
    viewport.addEventListener('click', (event) => {
      if (viewport.classList.contains('was-dragged')) event.preventDefault();
    }, true);
    // Sideways trackpad scrolling moves the row; up and down still scrolls the page
    viewport.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      this.x -= event.deltaX;
      this.wrap();
      this.paint();
      this.velocity = 0;
      this.resumeAt = performance.now() + 1200;
    }, { passive: false });
  }
}

if (!customElements.get('af-testimonials')) customElements.define('af-testimonials', AfTestimonials);
